import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, AlertTriangle, XCircle, Calendar as CalendarIcon } from 'lucide-react';

// Status badge config — drives both colour and sort order.
const STATUS_META = {
  AVAILABLE:   { label: 'Available',   cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle2,  rank: 0 },
  PARTIAL:     { label: 'Partial',     cls: 'bg-amber-100 text-amber-800 border-amber-300',       Icon: AlertTriangle, rank: 1 },
  BOOKED:      { label: 'Booked',      cls: 'bg-slate-200 text-slate-700 border-slate-300',       Icon: CalendarIcon,  rank: 2 },
  UNAVAILABLE: { label: 'Unavailable', cls: 'bg-red-100 text-red-800 border-red-300',             Icon: XCircle,       rank: 3 },
  UNKNOWN:     { label: '',            cls: 'bg-slate-50 text-slate-500 border-slate-200',        Icon: null,          rank: 4 },
};

export function AvailabilityStatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.UNKNOWN;
  if (!meta.label) return null;
  const Icon = meta.Icon;
  return (
    <span
      className={'inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border font-medium ' + meta.cls}
      data-testid={'availability-status-' + status.toLowerCase()}
    >
      {Icon ? <Icon className="w-2.5 h-2.5" /> : null}
      {meta.label}
    </span>
  );
}

/**
 * Hook: load availability badges for a list of staff against a given shift slot.
 *
 *   const { statusMap, sortedStaff, loading, enabled } = useStaffAvailabilityForShift(staff, { date, startTime, endTime, excludeShiftId });
 *
 * - Respects feature flag `scheduling_availability_visibility_enabled`
 * - Returns the original staff list (unsorted, statusMap empty) when the flag is off OR the API errors
 * - Sort order: AVAILABLE → PARTIAL → BOOKED → UNAVAILABLE → unknown, then by full_name
 */
export function useStaffAvailabilityForShift(staff, { date, startTime, endTime, excludeShiftId } = {}) {
  const [statusMap, setStatusMap] = useState({});
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);

  // Fetch flag once.
  useEffect(() => {
    let mounted = true;
    api.get('/production/feature-flags/mine').then(r => {
      const f = r.data?.data || r.data || {};
      if (mounted) setEnabled(f.scheduling_availability_visibility_enabled !== false);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const userIds = useMemo(() => (staff || []).map(s => s.user_id || s.id).filter(Boolean), [staff]);

  useEffect(() => {
    if (!enabled || !date || userIds.length === 0) { setStatusMap({}); return; }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ date, user_ids: userIds.join(',') });
    if (startTime) params.set('start_time', startTime);
    if (endTime) params.set('end_time', endTime);
    if (excludeShiftId) params.set('exclude_shift_id', excludeShiftId);
    api.get('/availability/check-bulk?' + params.toString())
      .then(r => { if (!cancelled) setStatusMap(r.data?.data || {}); })
      .catch(() => { if (!cancelled) setStatusMap({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, date, startTime, endTime, excludeShiftId, userIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedStaff = useMemo(() => {
    if (!enabled || Object.keys(statusMap).length === 0) return staff || [];
    const arr = (staff || []).slice();
    arr.sort((a, b) => {
      const ka = a.user_id || a.id;
      const kb = b.user_id || b.id;
      const sa = statusMap[ka]?.status || 'UNKNOWN';
      const sb = statusMap[kb]?.status || 'UNKNOWN';
      const ra = STATUS_META[sa]?.rank ?? 4;
      const rb = STATUS_META[sb]?.rank ?? 4;
      if (ra !== rb) return ra - rb;
      return (a.full_name || '').localeCompare(b.full_name || '');
    });
    return arr;
  }, [staff, statusMap, enabled]);

  return { statusMap, sortedStaff, loading, enabled };
}

/**
 * Drop-in replacement for the staff <Select> in the Drawer / Create modal.
 * Renders each option with a status badge when the feature flag is on.
 */
export function StaffSelectWithAvailability({
  staff,
  value,
  onValueChange,
  placeholder = 'Select staff',
  filter,           // optional (s) => boolean
  triggerClass,
  testId,
  date,
  startTime,
  endTime,
  excludeShiftId,
}) {
  const filtered = useMemo(() => {
    return (staff || []).filter(s => (filter ? filter(s) : true));
  }, [staff, filter]);

  const { statusMap, sortedStaff, enabled } = useStaffAvailabilityForShift(filtered, {
    date, startTime, endTime, excludeShiftId,
  });

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className={triggerClass} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {sortedStaff.map(s => {
          const key = s.user_id || s.id;
          const status = enabled ? (statusMap[key]?.status) : null;
          return (
            <SelectItem key={s.id} value={key}>
              <span className="inline-flex items-center gap-2 w-full">
                <span className="truncate">{s.full_name}</span>
                {status ? <AvailabilityStatusBadge status={status} /> : null}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
